/* import __COLOCATED_TEMPLATE__ from './sq-form-field.hbs'; */
/**
## Form Field

`SqFormFieldComponent` is a block component that's intended to be a wrapper around
other form elements, most commonly other form related components. A form field is
generally the most atomic portion of a field that gets moved around. It brings in an
inline label along for the ride, and you place whatever form elements/components you
want inside the block to define what's alongside the label. By default it stretches
itself out with the label to the immediate left of the input field. At narrow widths,
the label stacks on top of the input. Its state - default, focused, disabled, read only,
or invalid - visually affects the elements inside.

The critical parameter you should almost always be setting is `label`. You can also define
the form field sans label by setting `noLabel` to true.

Fields can be wrapped in [`sq-form`](/components/glass/sq-form.html) to enable
error messaging on focus and submit on enter.

```example
@title Basic Form Field with Field Text Area
@description We're missing a few borders because atomic form fields are designed to be in form rows and tables.

@controller-properties
{
}

@hbs
{{#sq-form-field label="Sample Label"}}
  {{sq-field-textarea placeholder=(t "userSettings.name")}}
{{/sq-form-field}}
```

## Form Field States
A Form Field's state - default, focused, disabled, read only, or invalid - visually affects
the elements inside. You pass parameters to set - `isInvalid`, `isDisabled`, `isReadOnly`, `isInvalid` -
accordingly.

```example

@controller-properties
{
}

@hbs
{{#sq-form-field label="Sample Label"}}
  {{sq-field-input placeholder=(t "common.firstName")}}
{{/sq-form-field}}
{{#sq-form-field label="Disabled" isDisabled=true}}
  {{sq-field-input placeholder=(t "common.lastName") disabled=true}}
{{/sq-form-field}}
{{#sq-form-field label="Read Only" isReadOnly=true}}
  {{sq-field-input placeholder=(t "common.email") disabled=true}}
{{/sq-form-field}}
{{#sq-form-field label="Error" isInvalid=true}}
  {{sq-field-input placeholder=(t "common.phone")}}
{{/sq-form-field}}
```

## Long Content

Padding is always added by default around both the label and input to ensure no visual collisions
around especially lengthy content.

```example
@controller-properties
{
}

@hbs
{{#sq-form-field label="Sample Label This Has A Lot Of Content Here"}}
  {{sq-field-input value="Lorem ipsum dolor sit amet, consectetur adipisicing elit. Libero, nisi molestiae cupiditate esse asperiores voluptates? Molestiae expedita illum laborum recusandae excepturi ratione cupiditate in iusto, nulla, dolores laboriosam cumque dolore."}}
{{/sq-form-field}}
```

## Group Ratios

By default there's a roughly 3 to 1 proportional split between the input field and the label. To alter this to an
even 1 to 1 split, add class `form-field--split-even`. Or, in cases where there isn't a label at all you'll get
100% width.

```example

@controller-properties
{
}

@hbs
{{#sq-form-field label="Default Size"}}
  {{sq-field-input placeholder=(t "common.firstName")}}
{{/sq-form-field}}
{{#sq-form-field label="Even Split" class="form-field--split-even"}}
  {{sq-field-input placeholder=(t "common.lastName")}}
{{/sq-form-field}}
{{#sq-form-field noLabel=true}}
  {{sq-field-input placeholder=(t "common.email")}}
{{/sq-form-field}}
```

## Tooltip

You can display a tooltip next to the field label by passing text as `tooltip` to the form field.

```example

@controller-properties
{
}

@hbs
{{#sq-form-field label="Complicated Field"
                 tooltip="Explanation of field"}}
  {{sq-field-input placeholder=(t "common.firstName")}}
{{/sq-form-field}}
```

## Top Label Variant

You can trigger the top label form variant by passing `useTopLabelVariant=true`.

```example
@hbs
{{#sq-form-field label="Put Me Up Top!"
                 useTopLabelVariant=true}}
  {{sq-field-input placeholder=(t "common.firstName")}}
{{/sq-form-field}}
```

##  Top Label Variant Layout via `sq-form`and `topLabelResponsiveColumnSpan`

The top label variant relies on [`.responsive-columns`](/glass/docs/core-styling/responsive-columns.html) for layout.
By default it will add the base `.responsive-column` class and the `.responsive-column--half` class to all top label
variant fields, but you can override this default sizing by passing any of the variants defined by the `.responsive-column`
CSS module (see above link for all of them) via the `topLabelResponsiveColumnSpan` parameter. For example, passing `third` will
result in `.responsive-column` and `.responsive-column--third` being added to your field.

You also need to wrap your fields in an element that has the `.responsive-columns` class on it in order for the grid
to work. The easiest way to do this is to pass `useTopLabelVariant=true` to [`sq-form`](/components/glass/sq-form.html) and use the yielded field component.
The yielded fields will also already be set up to use the top label variant, so you don't have to re-pass the `useTopLabelVariant`
to each of them individually.

```example
@hbs
{{#sq-form useTopLabelVariant=true
           as |form|}}
  {{#form.field label="First Name"}}
    {{sq-field-input placeholder="Matt"}}
  {{/form.field}}
  {{#form.field label="Last Name"}}
    {{sq-field-input placeholder="Wright"}}
  {{/form.field}}
  {{#form.field label="City"
                topLabelResponsiveColumnSpan="two-thirds"}}
    {{sq-field-input placeholder="San Francisco"}}
  {{/form.field}}
  {{#form.field label="State"
                topLabelResponsiveColumnSpan="third"}}
    {{sq-field-input placeholder="CA"}}
  {{/form.field}}
{{/sq-form}}
```

### Parameters
@param {String} label - String value of inner field.
@param {Boolean} [noLabel=false] - When set to `true`, no label will appear, 100% width will be dedicated to form field content.
@param {Boolean} [emptyLabel=false] - When set to `true`, a label will appear, but it will be empty.
@param {Boolean} [isDisabled=false] - Gives the form field a disabled appearance. Don't forget to also set any inner form elements to `disabled`.
@param {Boolean} [isReadOnly=false] - Gives the form field a read only appearance. Don't forget to also set any inner form elements to `disabled`.
@param {Boolean} [isInvalid=false] - Whether the field is currently in an invalid state.
@param {Boolean} [errorMessage=null] - The error message for the field. Currently unused, but Design is exploring options for field-level error messaging, so we're keeping it around for now.
@param {Boolean} [showValidation=true] - Determines whether or not errors are currently being shown.
@param {String} [tooltip=null] - If present, display a tooltip icon with passed text next to field label.
@param {Component} [tooltipComponent=null] - If present, display a tooltip Component next to field label.
@param {Boolean} [useTopLabelVariant=false] - Triggers the top label form variant UI, with label on top and input below.
@param {String} [topLabelResponsiveColumnSpan='half'] - Width keyword used for generating a `.responsive-column--{{width}}` class, see [`here`](/glass/docs/core-styling/responsive-columns.html) for examples. Only added if `useTopLabelVariant` is `true`.

Sass dependencies at:
`app/assets/stylesheets/bridgeport/components/form-elements/formfield/_formfield.sass`
*/

import { isBlank } from '@ember/utils';
import { assert } from '@ember/debug';
import { cancel, once, scheduleOnce } from '@ember/runloop';
import { and } from '@ember/object/computed';
import { computed } from '@ember/object';
import Component from '@ember/component';
import { A as EmberArray } from '@ember/array';
import SqFieldCheckboxesComponent from '@square/glass-ui/components/sq-field-checkboxes';
import SqFieldSwitchComponent from '@square/glass-ui/components/sq-option-switch';
import SqFieldRadioSetComponent from '@square/glass-ui/components/sq-field-radio-set';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: 'form-field',
  classNameBindings: ['shouldShowErrorState:form-field--is-invalid', 'isDisabled:form-field--is-disabled', 'isFocused:form-field--is-focused', 'isReadOnly:form-field--is-read-only', 'withCheckboxes:form-field--with-checkboxes', 'withSwitches:form-field--with-switches', 'withRadioSet:form-field--with-radio-set', 'noLabel:form-field--no-label', 'withTextOnly:form-field--with-text-only', 'useTopLabelVariant:form-field--top-label', 'responsiveColumnClass'],
  managedFields: computed(function () {
    return EmberArray([]);
  }),
  // NOTE: isInvalid, isDisabled and isReadOnly are not included here because
  // we check hasOwnProperty on them in init.
  // TODO(mattwright): Remove init aggregate behavior and add defaults here.
  noLabel: false,
  emptyLabel: false,
  errorMessage: null,
  showValidation: true,
  tooltip: null,
  // The following attributes are only relevant for the top label form variant
  useTopLabelVariant: false,
  topLabelResponsiveColumnSpan: 'half',
  // Aggregate properties
  withCheckboxes: false,
  withSwitches: false,
  withRadioSet: false,
  withTextOnly: false,
  shouldShowErrorState: and('showValidation', 'isInvalid'),
  init(...args) {
    this._super(...args);

    // FIXME: These are observers instead of computeds so that we can use
    // `Ember.run` to schedule the property updates, which is a hack to avoid a
    // backtracking render error that began throwing in Ember 2.10. We should
    // really try to remove this kind of munging of the outer parent by the
    // child, probably by just explicitly putting CSS class names in the
    // templates rather than detecting.

    // Note that we only set up observers if the property is not passed directly
    /* eslint-disable ember/no-observers, no-prototype-builtins */
    if (!this.hasOwnProperty('isDisabled')) {
      this.addObserver('managedFields.@each.isDisabled', this, () => {
        scheduleOnce('afterRender', this, 'updateAggregateIsDisabled');
      });
    }
    if (!this.hasOwnProperty('isFocused')) {
      this.addObserver('managedFields.@each.isFocused', this, () => {
        scheduleOnce('afterRender', this, 'updateAggregateIsFocused');
      });
    }
    if (!this.hasOwnProperty('isInvalid')) {
      this.addObserver('managedFields.@each.isInvalid', this, () => {
        scheduleOnce('afterRender', this, 'updateAggregateIsInvalid');
      });
    }
    if (!this.hasOwnProperty('isReadOnly')) {
      this.addObserver('managedFields.@each.isReadOnly', this, () => {
        scheduleOnce('afterRender', this, 'updateAggregateIsReadOnly');
      });
    }
    /* eslint-enable ember/no-observers, no-prototype-builtins */
  },
  destroy(...args) {
    cancel(this._focusMessageTask);
    if (this._didSetFocusedMessage) {
      once(this.get('flashContext'), this.get('flashContext').clearFocusedMessage);
    }
    this._super(...args);
  },
  didInsertElement(...args) {
    this._super(...args);
    assert('Form fields require either a label or the "noLabel" setting turned on.', !isBlank(this.get('label')) || this.get('noLabel') || this.get('emptyLabel'));

    // Set modifiers if we detect checkboxes or switches inside the form field block element, affect spacing/padding
    const childViews = this.get('childViews');
    for (const childView of childViews) {
      if (childView instanceof SqFieldCheckboxesComponent) {
        this.set('withCheckboxes', true);
      }
      if (childView instanceof SqFieldSwitchComponent) {
        this.set('withSwitches', true);
      }
      if (childView instanceof SqFieldRadioSetComponent) {
        this.set('withRadioSet', true);
      }
    }
  },
  didUpdateAttrs() {
    this._super();
    if (!this.get('flashContext')) {
      return;
    }
    const errorMessage = this.get('errorMessage');
    if (this.get('isFocused') && errorMessage) {
      cancel(this._focusMessageTask);
      // We have to schedule these or Ember will throw a backtracking
      // render error due to `flash.currentMessage` already being set
      this._focusMessageTask = once(this, this._setFocusedMessage, errorMessage);
    } else if (this._didSetFocusedMessage && !errorMessage) {
      cancel(this._focusMessageTask);
      this._focusMessageTask = once(this, this._clearFocusedMessage);
    }
  },
  responsiveColumnClass: computed('useTopLabelVariant', 'topLabelResponsiveColumnSpan', function () {
    // only applied for top label variant
    return this.useTopLabelVariant ? `responsive-column responsive-column--${this.topLabelResponsiveColumnSpan}` : false;
  }),
  focusIn() {
    if (!this.get('flashContext')) {
      return;
    }
    const errorMessage = this.get('errorMessage');
    if (errorMessage) {
      once(this, this._setFocusedMessage, errorMessage);
    }
  },
  focusOut() {
    if (!this.get('flashContext')) {
      return;
    }
    if (this._didSetFocusedMessage) {
      once(this, this._clearFocusedMessage);
    }
  },
  _setFocusedMessage(errorMessage) {
    this.get('flashContext').setFocusedMessage(errorMessage);
    this._didSetFocusedMessage = true;
  },
  _clearFocusedMessage() {
    this.get('flashContext').clearFocusedMessage();
    this._didSetFocusedMessage = false;
  },
  // These are all separate functions so we can pass them to
  // `Ember.run.scheduleOnce` and have them only be called once per runloop.
  updateAggregateIsDisabled() {
    this.updateAggregateProperty('isDisabled');
  },
  updateAggregateIsFocused() {
    this.updateAggregateProperty('isFocused');
  },
  updateAggregateIsInvalid() {
    this.updateAggregateProperty('isInvalid');
  },
  updateAggregateIsReadOnly() {
    this.updateAggregateProperty('isReadOnly');
  },
  updateAggregateProperty(property) {
    if (this.isDestroyed || this.isDestroying) {
      return;
    }
    this.set(property, this.get('managedFields').isAny(property));
  },
  addManagedField(field) {
    this.get('managedFields').addObject(field);
  },
  removeManagedField(field) {
    this.get('managedFields').removeObject(field);
  }
});